/**
 * Documents Page
 *
 * Document management view for loan-related documents. Lists documents
 * with status, type, date columns in a DataGrid. Provides actions for
 * generating, downloading, and sending documents for signature.
 */
import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { advisorClient } from '../api/client';
import { AdvisorServiceMissingError, AdvisorServiceNotice, advisorFailure } from '../components/AdvisorServiceNotice';
import type { components } from '@aaimplatform/institutional-types/advisor-tools';
import type { ColumnDef, SelectOption } from '@aaimplatform/ui';
import {
  BodyText,
  Card,
  MetricCard,
  DashboardGrid,
  DashboardWidget,
  DataGrid,
  Select,
} from '@aaimplatform/ui';


type LoanDocument = components['schemas']['LoanDocument'];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DOCUMENT_TYPE_OPTIONS: SelectOption[] = [
  { value: '', label: 'All Types' },
  { value: 'loan_agreement', label: 'Loan Agreement' },
  { value: 'collateral_assessment', label: 'Collateral Assessment' },
  { value: 'ucc_filing', label: 'UCC Filing' },
  { value: 'valuation_report', label: 'Valuation Report' },
  { value: 'disclosure_statement', label: 'Disclosure Statement' },
  { value: 'amendment', label: 'Amendment' },
];

const STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'sent', label: 'Sent for Signature' },
  { value: 'signed', label: 'Signed' },
  { value: 'archived', label: 'Archived' },
];

const STATUS_BADGE_STYLES: Record<string, string> = {
  draft: 'bg-recessed text-tertiary',
  pending_review: 'bg-accent/10 text-accent',
  approved: 'bg-growth/10 text-growth',
  sent: 'bg-warning/10 text-warning',
  signed: 'bg-growth/10 text-growth',
  archived: 'bg-recessed text-secondary',
};

const TYPE_LABELS: Record<string, string> = {
  loan_agreement: 'Loan Agreement',
  collateral_assessment: 'Collateral Assessment',
  ucc_filing: 'UCC Filing',
  valuation_report: 'Valuation Report',
  disclosure_statement: 'Disclosure Statement',
  amendment: 'Amendment',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentsPage() {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data: documents, isLoading, error } = useQuery({
    queryKey: ['loan-documents', typeFilter, statusFilter],
    queryFn: async (): Promise<LoanDocument[]> => {
      const { data, error, response } = await advisorClient.GET('/api/documents', {
        params: {
          query: {
            ...(typeFilter ? { type: typeFilter } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
          },
        },
      });
      if (error || !response.ok) throw advisorFailure(response, error, 'Failed to load documents');
      return (data ?? []) as LoanDocument[];
    },
  });

  // Generate document mutation
  const generateDocument = useMutation({
    mutationFn: async (documentId: string) => {
      const { data, error } = await advisorClient.POST('/api/documents/{documentId}/generate', {
        params: { path: { documentId } },
      });
      if (error) throw new Error('Failed to generate document');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-documents'] });
    },
  });

  // Send for signature mutation
  const sendForSignature = useMutation({
    mutationFn: async (documentId: string) => {
      const { data, error } = await advisorClient.POST('/api/documents/{documentId}/send', {
        params: { path: { documentId } },
      });
      if (error) throw new Error('Failed to send document for signature');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-documents'] });
    },
  });

  // Summary metrics
  const metrics = useMemo(() => {
    if (!documents || documents.length === 0) {
      return { total: 0, pendingReview: 0, awaitingSignature: 0, signed: 0 };
    }
    return {
      total: documents.length,
      pendingReview: documents.filter((d) => d.status === 'pending_review').length,
      awaitingSignature: documents.filter((d) => d.status === 'sent').length,
      signed: documents.filter((d) => d.status === 'signed').length,
    };
  }, [documents]);

  const handleDownload = useCallback(async (documentId: string, fileName: string) => {
    // Direct fetch for binary download -- not a JSON API call. credentials:'include'
    // so the HttpOnly session cookie authenticates the request (cookie-session cutover).
    const response = await fetch(`/api/documents/${documentId}/download`, { credentials: 'include' });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }, []);

  const columns = useMemo<ColumnDef<LoanDocument, unknown>[]>(() => [
    {
      id: 'title',
      header: 'Document',
      accessorKey: 'title',
      size: 240,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="font-semibold text-primary">{String(getValue())}</span>
      ),
    },
    {
      id: 'documentType',
      header: 'Type',
      accessorKey: 'documentType',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="text-primary">{TYPE_LABELS[String(getValue())] ?? String(getValue())}</span>
      ),
    },
    {
      id: 'clientName',
      header: 'Client',
      accessorKey: 'clientName',
      size: 160,
      enableSorting: true,
    },
    {
      id: 'loanNumber',
      header: 'Loan #',
      accessorKey: 'loanNumber',
      size: 110,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="font-mono text-primary">{String(getValue())}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 140,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => {
        const status = String(getValue());
        return (
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${
              STATUS_BADGE_STYLES[status] ?? 'bg-recessed text-secondary'
            }`}
          >
            {status.replace(/_/g, ' ')}
          </span>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessorKey: 'createdAt',
      size: 120,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => formatDate(String(getValue())),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      size: 200,
      cell: ({ row }: { row: { original: LoanDocument } }) => {
        const doc = row.original;
        return (
          <div className="flex gap-2 items-center">
            {doc.status === 'draft' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  generateDocument.mutate(doc.id);
                }}
                className="text-accent text-xs hover:underline"
                disabled={generateDocument.isPending}
              >
                Generate
              </button>
            )}
            {(doc.status === 'approved' || doc.status === 'signed') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(doc.id, `${doc.title}.pdf`);
                }}
                className="text-accent text-xs hover:underline"
              >
                Download
              </button>
            )}
            {doc.status === 'approved' && (
              <>
                <span className="text-tertiary">|</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    sendForSignature.mutate(doc.id);
                  }}
                  className="text-accent text-xs hover:underline"
                  disabled={sendForSignature.isPending}
                >
                  Send for Signature
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ], [generateDocument, sendForSignature, handleDownload]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <BodyText className="text-secondary mt-2">
            Manage loan agreements, collateral assessments, UCC filings, and valuation reports
          </BodyText>
        </div>
      </div>

      {/* Summary Metrics */}
      <DashboardGrid columns={4} gap="compact" className="mb-6">
        <DashboardWidget id="total-documents">
          <MetricCard label="Total Documents" value={String(metrics.total)} valueColor="primary" />
        </DashboardWidget>
        <DashboardWidget id="pending-review">
          <MetricCard label="Pending Review" value={String(metrics.pendingReview)} valueColor="accent" />
        </DashboardWidget>
        <DashboardWidget id="awaiting-signature">
          <MetricCard label="Awaiting Signature" value={String(metrics.awaitingSignature)} valueColor="accent" />
        </DashboardWidget>
        <DashboardWidget id="signed">
          <MetricCard label="Signed" value={String(metrics.signed)} valueColor="growth" />
        </DashboardWidget>
      </DashboardGrid>

      {/* Filters */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Document Type"
            options={DOCUMENT_TYPE_OPTIONS}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          />
          <Select
            label="Status"
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          />
        </div>
      </Card>

      {/* Documents Grid */}
      {error instanceof AdvisorServiceMissingError ? (
        <AdvisorServiceNotice />
      ) : error ? (
        <Card>
          <div className="p-4 bg-error/10 border border-error/20 rounded">
            <BodyText className="text-error font-semibold">Error Loading Documents</BodyText>
            <BodyText className="text-error mt-1">
              Failed to retrieve document list.
            </BodyText>
          </div>
        </Card>
      ) : (
        <DataGrid
          data={documents ?? []}
          columns={columns}
          isLoading={isLoading}
          enableGlobalFilter
          enableColumnFilters
          enableColumnVisibility
          enablePagination
          defaultPageSize={20}
          pageSizeOptions={[10, 20, 50]}
          enableExport
          striped
          hoverable
          density="default"
          getRowId={(row) => row.id}
          emptyMessage="No documents found. Documents are generated during the lending workflow."
        />
      )}
    </div>
  );
}
